import { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { CONFIRM_CLOCK_ROLL_MS } from './korza-studio';

// Each digit column holds this many 0–9 cycles, enough for a full extra
// turn on every change the Studio makes (the sample time, then the launch).
const CYCLES = 4;
const COLUMN = Array.from({ length: CYCLES * 10 }, (_, i) => i % 10);
const STAGGER_S = 0.08;
// Even in and out, so the digits are seen rolling (not just landing).
const EASE = [0.45, 0, 0.15, 1] as const;

/**
 * One digit that rolls forward to `digit`, one extra turn included. A column
 * that appears after the first render (`enters`) starts at 0 and rolls too.
 */
function DigitRoller({
  digit,
  order,
  enters,
}: {
  digit: number;
  order: number;
  enters: boolean;
}) {
  const reduceMotion = useReducedMotion();
  // Row of COLUMN on show; moves forward only, so the roll never rewinds.
  const [roll, setRoll] = useState(() => ({
    digit: enters ? 0 : digit,
    position: enters ? 0 : digit,
  }));
  if (roll.digit !== digit) {
    const forward = (digit - (roll.position % 10) + 10) % 10;
    setRoll({
      digit,
      position: Math.min(
        roll.position + forward + 10,
        COLUMN.length - 10 + digit
      ),
    });
  }

  return (
    <span className="relative inline-block h-[1em] overflow-hidden">
      {/* Width of the widest digit; the column itself is absolute. */}
      <span className="invisible">0</span>
      <motion.span
        aria-hidden
        className="absolute inset-x-0 top-0 flex flex-col items-center"
        initial={enters ? { y: '0em' } : false}
        animate={{ y: `${-roll.position}em` }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : {
                duration: CONFIRM_CLOCK_ROLL_MS / 1000,
                delay: order * STAGGER_S,
                ease: EASE,
              }
        }
      >
        {COLUMN.map((d, i) => (
          <span key={i} className="h-[1em] leading-none">
            {d}
          </span>
        ))}
      </motion.span>
    </span>
  );
}

/**
 * The lock screen clock as an odometer: changing `value` ("9:41" → "19:00")
 * rolls each digit to its new value. Columns are keyed from the right, so a
 * digit added on the left opens its room (width, on this one small element:
 * motion's `layout` would measure the whole document on every commit) and
 * fades in rolling from 0.
 */
export function RollingClock({ value }: { value: string }) {
  const reduceMotion = useReducedMotion();
  const [firstLength] = useState(value.length);
  const chars = Array.from(value);
  return (
    <span className="inline-flex tabular-nums" aria-label={value} role="img">
      {chars.map((char, i) => {
        const fromRight = chars.length - 1 - i;
        const enters = fromRight >= firstLength && !reduceMotion;
        const content = /\d/u.test(char) ? (
          <DigitRoller digit={Number(char)} order={i} enters={enters} />
        ) : (
          char
        );
        return enters ? (
          <motion.span
            key={fromRight}
            aria-hidden
            className="inline-block overflow-hidden"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 'auto', opacity: 1 }}
            transition={{ duration: CONFIRM_CLOCK_ROLL_MS / 2000, ease: EASE }}
          >
            {content}
          </motion.span>
        ) : (
          <span key={fromRight} aria-hidden className="inline-block">
            {content}
          </span>
        );
      })}
    </span>
  );
}
